/**
 * ValidationAlertDialog — the pop-up that tells a parent what is missing or wrong after they
 * click an always-clickable submit button.
 *
 * Always-clickable buttons (2026-08-04): RegisterPage's Sign Up and Continue with Google
 * buttons no longer disable themselves on an incomplete form. This dialog replaces that
 * silent greying out, so every blocked click produces an explanation instead of nothing.
 *
 * Cloned from the house overlay pattern (DemoLimitPopup to ReportIssueDialog to
 * EndSessionConfirmDialog): fixed overlay, lazy DOM, auto-focus on open, Escape to dismiss,
 * Tab trapped inside. No @radix-ui dialog dependency is installed and none is added here.
 *
 * ONE DELIBERATE DEVIATION from those three: role="alertdialog", not role="dialog". This
 * interrupts the parent with an error they did not ask to see, which is exactly what
 * alertdialog means in ARIA; the others are confirmations, where dialog is correct. It also
 * carries aria-labelledby/aria-describedby so the title and the issue list are announced on
 * open rather than just the aria-label.
 *
 * Single action by design. There is nothing to confirm or cancel: the parent acknowledges,
 * the dialog closes, and RegisterPage moves focus to the first offending field. Focus is
 * moved by the CALLER after unmount, never from in here, so it cannot fight the focus trap.
 *
 * Props are fully controlled. No data fetching, no navigation.
 * House style: copy contains no dashes.
 */

import { useCallback, useEffect, useId, useRef } from 'react';
import { Button } from '@/components/ui/button';

export interface ValidationAlertDialogProps {
  /** Controls visibility; returns null when false (lazy DOM, per the house overlay pattern). */
  isVisible: boolean;

  /**
   * What the parent needs to fix, in the visual order of the fields they belong to. The first
   * entry is the one the caller focuses on dismiss, so the order is not cosmetic.
   */
  issues: string[];

  /** Called on the acknowledge button, Escape, or an overlay click. */
  onDismiss: () => void;
}

const TITLE = 'Almost there';

export function ValidationAlertDialog({ isVisible, issues, onDismiss }: ValidationAlertDialogProps) {
  const dismissButtonRef = useRef<HTMLButtonElement>(null);
  // useId keeps the title/description ids unique if this ever renders more than once.
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-description`;

  // Auto-focus the acknowledge button when the dialog opens (house pattern: 50ms lets the
  // element mount and paint first).
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        dismissButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Escape dismisses. Tab is swallowed because there is exactly one focusable control in
  // here, so the trap is simply "keep focus where it is" rather than a two-button cycle.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        dismissButtonRef.current?.focus();
      }
    },
    [onDismiss]
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onKeyDown={handleKeyDown}
      // Overlay click dismisses. The check keeps a click that started inside the card from
      // closing it (the card is a child, so the event bubbles up to here).
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onDismiss();
        }
      }}
      tabIndex={-1}
    >
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 max-w-sm mx-4">
        <div className="text-3xl md:text-4xl mb-3 text-center" aria-hidden="true">
          ⚠️
        </div>

        <h2 id={titleId} className="text-lg md:text-xl font-bold text-gray-900 mb-2 text-center">
          {TITLE}
        </h2>

        <div id={descriptionId} className="mb-5">
          <p className="text-sm md:text-base text-gray-600 leading-relaxed text-center">
            {issues.length === 1
              ? 'Please check this before you continue:'
              : 'Please check the following before you continue:'}
          </p>

          {/* A single issue reads better as a sentence than as a one item bullet list. */}
          {issues.length === 1 ? (
            <p className="mt-3 text-sm md:text-base font-medium text-gray-900 text-center">
              {issues[0]}
            </p>
          ) : (
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm md:text-base text-gray-900">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
        </div>

        <Button
          ref={dismissButtonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          variant="warm"
          className="w-full h-11 rounded-[10px]"
        >
          Got it
        </Button>
      </div>
    </div>
  );
}
